import * as React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Userdata } from "@/types/user";
import { SelectUsers } from "@/components/share/components/SelectUsers";
import type { ShareRole } from "@/components/share/types";

interface Props {
  isReadOnly: boolean;
  isNewUserSelected: boolean;
  setIsNewUserSelected: (isNewUserSelected: boolean) => void;
  onShare: (userIds: string[], role: ShareRole) => Promise<void>;
  successMessage: string;
}

interface FormInputs {
  users: Userdata[];
  role: ShareRole;
}

export const ShareUsersForm: React.FC<Props> = ({
  isReadOnly,
  isNewUserSelected,
  setIsNewUserSelected,
  onShare,
  successMessage,
}) => {
  const methods = useForm<FormInputs>({
    defaultValues: {
      users: [],
    },
  });

  const users = useWatch({
    control: methods.control,
    name: "users",
  });

  React.useEffect(() => {
    setIsNewUserSelected(users.length > 0);
  }, [users, setIsNewUserSelected]);

  const onSubmit = async (data: FormInputs) => {
    try {
      await onShare(
        data.users.map(user => user.id),
        data.role,
      );
      toast.success(successMessage);
      methods.resetField("users");
      setIsNewUserSelected(false);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to share. Please try again.");
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="flex">
          <div className="w-5/6">
            <SelectUsers isReadOnly={isReadOnly} />
          </div>
          <div className="w-1/6">
            <select
              disabled={isReadOnly}
              style={{
                lineHeight: "26px",
              }}
              {...methods.register("role")}
              className="ml-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
            >
              <option value="owner">Owner</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
        </div>
        {isNewUserSelected && (
          <DialogFooter className="mt-3">
            <Button variant="default" type="submit" disabled={methods.formState.isSubmitting}>
              {methods.formState.isSubmitting && <CircleLoader />}
              {methods.formState.isSubmitting ? "Saving" : "Share"}
            </Button>
          </DialogFooter>
        )}
      </form>
    </FormProvider>
  );
};
